// Google Analytics tracking hook
// GA is loaded in index.html with the site's ID (SiteConfig.analytics.ga4Id).
import { SITE } from '@/site';

const GA_ID = SITE.analytics.ga4Id;

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Track page views for SPA navigation
export function trackPageView(path: string, title?: string) {
  if (GA_ID && typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_ID, {
      page_path: path,
      page_title: title,
    });
  }
}

// Track custom events
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
) {
  if (GA_ID && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

// Specific event trackers
export const analytics = {
  // Resort interactions
  resortClick: (resortName: string, resortSlug: string) => {
    trackEvent('select_resort', {
      resort_name: resortName,
      resort_slug: resortSlug,
    });
  },

  resortView: (resortName: string, resortSlug: string) => {
    trackEvent('view_resort', {
      resort_name: resortName,
      resort_slug: resortSlug,
    });
  },

  // Webcam interactions
  webcamView: (cameraName: string, resortName?: string, streamType?: string) => {
    trackEvent('view_webcam', {
      camera_name: cameraName,
      resort_name: resortName || 'unknown',
      stream_type: streamType || 'unknown',
    });
  },

  webcamPlay: (cameraName: string, resortName?: string, streamType?: string) => {
    trackEvent('play_webcam', {
      camera_name: cameraName,
      resort_name: resortName || 'unknown',
      stream_type: streamType || 'unknown',
    });
  },

  // Currency changes
  currencyChange: (fromCurrency: string, toCurrency: string) => {
    trackEvent('change_currency', {
      from_currency: fromCurrency,
      to_currency: toCurrency,
    });
  },

  // Affiliate outbound clicks (mark affiliate_click as a key event in GA4)
  affiliateClick: (partner: string, hotelId: string) => {
    trackEvent('affiliate_click', {
      partner,
      hotel_id: hotelId,
      page_path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  },



  // Language changes
  languageChange: (fromLanguage: string, toLanguage: string) => {
    trackEvent('change_language', {
      from_language: fromLanguage,
      to_language: toLanguage,
    });
  },

  // Map interactions
  mapRegionClick: (region: string) => {
    trackEvent('click_map_region', {
      region: region,
    });
  },

  // External links
  externalLinkClick: (url: string, context?: string) => {
    trackEvent('click_external_link', {
      link_url: url,
      context: context || 'unknown',
    });
  },

  // Search
  search: (query: string, resultsCount: number) => {
    trackEvent('search', {
      search_term: query,
      results_count: resultsCount,
    });
  },

  // Filter usage
  filterUse: (filterType: string, filterValue: string) => {
    trackEvent('use_filter', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  },
};

export default analytics;
